import { useEffect, useRef, useState } from "react";
import { IoIosArrowDown } from "react-icons/io";
import imageCompression from "browser-image-compression";
import { getUploadUrl } from "src/Common/common";
import { handleError } from "src/Common/function";
import Loader from "src/Components/Common/Loader";
import WOService from "src/Services/API/wo.service";
import AttachmentImageSlider from "src/Pages/WO/Components/attachment/AttachmentImageSlider";
import { compressIfNeeded } from "src/Pages/WO/Components/upload/uploadModalUI";

let uidCounter = 0;
const newUid = () => {
  uidCounter += 1;
  return `rec-local-${uidCounter}-${Date.now()}`;
};

// The saved items as this list's rows ({ uid, id?, text }). There is always at
// least one box, so where a recommendation goes is never hidden.
export const recommendationRows = (wo) => {
  const saved = wo?.recommendationItems;
  const rows = Array.isArray(saved)
    ? saved.map((item) => ({
        uid: `rec-${item.id}`,
        id: item.id,
        text: item.text ?? "",
        photos: item.photos ?? [],
      }))
    : wo?.recommendations
      ? [
          {
            uid: newUid(),
            text: String(wo.recommendations)
              .replace(/<[^>]+>/g, " ")
              .trim(),
          },
        ]
      : [];
  return rows.length > 0 ? rows : [{ uid: newUid(), text: "" }];
};

// For the save: the whole list, blank boxes left out.
export const recommendationsFull = (items) =>
  JSON.stringify(
    items
      .map(({ id, text, photos }) => ({
        id,
        text: (text || "").trim(),
        photos: photos ?? [],
      }))
      .filter((item) => item.text),
  );

// Nothing in it: no words and no photos.
const isBlank = (item) => !item?.text?.trim() && !(item?.photos?.length > 0);

// One recommendation's photos: thumbnails (click to view, x to remove), a
// spinner tile per upload in flight, and a "+ Photo" tile.
function RecommendationPhotos({
  photos,
  uploading,
  disabled,
  onFiles,
  onRemove,
  onOpen,
}) {
  return (
    <div className="flex flex-wrap gap-2.5 mt-2.5">
      {photos.map((name, index) => (
        <div key={name} className="relative w-16 h-16">
          <button
            type="button"
            onClick={() => onOpen(index)}
            className="w-16 h-16 cursor-pointer"
          >
            <img
              src={getUploadUrl(name)}
              alt=""
              className="w-16 h-16 rounded-md object-cover bg-[#E5E8F3] border border-[#E5E8F3]"
            />
          </button>
          {!disabled && (
            <button
              type="button"
              onClick={() => onRemove(index)}
              title="Remove photo"
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#1A2332] text-white font-OpenSansBold text-[12px] leading-none flex items-center justify-center cursor-pointer"
            >
              ×
            </button>
          )}
        </div>
      ))}
      {Array.from({ length: uploading }).map((_, index) => (
        <div
          key={`uploading-${index}`}
          className="w-16 h-16 rounded-md bg-[#F1F5F9] flex items-center justify-center"
        >
          <Loader size={20} />
        </div>
      ))}
      {!disabled && (
        <label className="w-16 h-16 rounded-md border border-dashed border-[#0065FD] flex flex-col items-center justify-center cursor-pointer text-[#0065FD]">
          <span className="font-OpenSansBold text-[18px] leading-5">+</span>
          <span className="font-OpenSansRegular text-[11px] leading-4">
            Photo
          </span>
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = [...(e.target.files || [])];
              e.target.value = "";
              if (files.length) onFiles(files);
            }}
          />
        </label>
      )}
    </div>
  );
}

/**
 * The service ticket's recommendations, one box each, the same as the
 * technician app's: each reaches the customer as its own item, which they can
 * turn into a service or quote request.
 *
 * One box is open at a time: "+ Add Recommendation" closes the one before it
 * and opening a closed one closes the rest. A closed box is one row with its
 * first line. Adding while a box is still blank goes back to that box, and
 * closing a blank one (when there are others) drops it.
 */
function RecommendationList({ items, onChange, disabled = false }) {
  const [openUid, setOpenUid] = useState(undefined);
  const inputRefs = useRef({});
  const focusUid = useRef(null);
  // Uploads in flight per box, and the photo being viewed ({ uid, index }).
  const [uploading, setUploading] = useState({});
  const [viewing, setViewing] = useState(null);

  const countUploading = (uid, change) =>
    setUploading((prev) => ({
      ...prev,
      [uid]: Math.max(0, (prev[uid] || 0) + change),
    }));

  // Uploads straight away; the names join the box and save with the ticket.
  const uploadPhotos = async (uid, files) => {
    countUploading(uid, files.length);
    try {
      const ready = await Promise.all(
        files.map((file) => compressIfNeeded(file, imageCompression)),
      );
      const response = await WOService.uploadRecommendationPhotos(ready);
      const names = response.data?.fileNames ?? [];
      onChange((prev) =>
        prev.map((item) =>
          item.uid === uid
            ? { ...item, photos: [...(item.photos ?? []), ...names] }
            : item,
        ),
      );
    } catch (error) {
      handleError(error);
    } finally {
      countUploading(uid, -files.length);
    }
  };

  const removePhoto = (uid, index) =>
    onChange((prev) =>
      prev.map((item) =>
        item.uid === uid
          ? {
              ...item,
              photos: (item.photos ?? []).filter((_, n) => n !== index),
            }
          : item,
      ),
    );

  const viewingPhotos = viewing
    ? (items.find((item) => item.uid === viewing.uid)?.photos ?? [])
    : [];

  const open =
    openUid === undefined
      ? items.length === 1
        ? items[0].uid
        : null
      : openUid;

  useEffect(() => {
    if (!focusUid.current) return;
    inputRefs.current[focusUid.current]?.focus();
    focusUid.current = null;
  }, [open, items.length]);

  const withoutBlank = (list, uid) =>
    list.length > 1 && uid && isBlank(list.find((item) => item.uid === uid))
      ? list.filter((item) => item.uid !== uid)
      : list;

  const toggle = (uid) => {
    if (open === uid) {
      setOpenUid(null);
      const next = withoutBlank(items, uid);
      if (next !== items) onChange(next);
      return;
    }
    const next = withoutBlank(items, open);
    if (next !== items) onChange(next);
    setOpenUid(uid);
  };

  const add = () => {
    const blank = items.find(isBlank);
    if (blank) {
      focusUid.current = blank.uid;
      setOpenUid(blank.uid);
      return;
    }
    const uid = newUid();
    focusUid.current = uid;
    onChange([...items, { uid, text: "" }]);
    setOpenUid(uid);
  };

  const remove = (uid) => {
    const next = items.filter((item) => item.uid !== uid);
    if (next.length === 0) {
      const fresh = newUid();
      onChange([{ uid: fresh, text: "" }]);
      setOpenUid(fresh);
      return;
    }
    onChange(next);
    if (open === uid) setOpenUid(null);
  };

  const edit = (uid, text) =>
    onChange(
      items.map((item) => (item.uid === uid ? { ...item, text } : item)),
    );

  const several = items.length > 1;

  return (
    <div className="w-full flex flex-col gap-2.5">
      {items.map((item, index) => {
        const isOpen = open === item.uid;
        const firstLine = (item.text || "").trim().split("\n")[0];
        return (
          <div
            key={item.uid}
            className="w-full rounded-md border border-[#E5E8F3] border-l-[4px] border-l-[#0065FD] bg-white"
          >
            {isOpen ? (
              <div className="px-3 pt-2 pb-3">
                {several || (!isBlank(item) && !disabled) ? (
                  <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-[#E5E8F3]">
                    <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#0065FD] uppercase">
                      {several ? `Recommendation ${index + 1}` : ""}
                    </span>
                    <div className="flex items-center gap-3">
                      {!disabled && (
                        <button
                          type="button"
                          onClick={() => remove(item.uid)}
                          className="font-OpenSansRegular text-[12px] leading-[18px] text-[#92400E] underline cursor-pointer"
                        >
                          Remove
                        </button>
                      )}
                      {several && (
                        <button
                          type="button"
                          onClick={() => toggle(item.uid)}
                          className="text-[#7C829A] cursor-pointer"
                          title="Collapse"
                        >
                          <IoIosArrowDown className="rotate-180" size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ) : null}
                <textarea
                  ref={(node) => {
                    inputRefs.current[item.uid] = node;
                  }}
                  value={item.text}
                  disabled={disabled}
                  onChange={(e) => edit(item.uid, e.target.value)}
                  placeholder="Describe one recommendation..."
                  className="w-full h-[96px] p-3 rounded-md bg-[#FBFCFF] border border-[#E5E8F3] outline-none focus:border-[#0065FD] resize-y text-[#1A2332] font-OpenSansRegular text-[14px] leading-5 tracking-[0.14px] placeholder:text-[#868DA6]"
                />
                <RecommendationPhotos
                  photos={item.photos ?? []}
                  uploading={uploading[item.uid] || 0}
                  disabled={disabled}
                  onFiles={(files) => uploadPhotos(item.uid, files)}
                  onRemove={(index) => removePhoto(item.uid, index)}
                  onOpen={(index) => setViewing({ uid: item.uid, index })}
                />
                {/* Only recommendations with words are saved. */}
                {item.photos?.length > 0 && !item.text?.trim() && (
                  <p className="mt-2 font-OpenSansRegular text-[12px] leading-[18px] text-[#92400E]">
                    Describe the recommendation so it's saved with its photos.
                  </p>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => toggle(item.uid)}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left cursor-pointer"
              >
                <span className="font-OpenSansSemiBold text-[12px] leading-5 text-[#0065FD]">
                  {index + 1}.
                </span>
                <span
                  className={`flex-1 min-w-0 truncate text-[14px] leading-5 ${
                    firstLine
                      ? "font-OpenSansRegular text-[#1A2332]"
                      : "font-OpenSansRegular text-[#868DA6]"
                  }`}
                >
                  {firstLine || "Empty"}
                </span>
                {item.photos?.length > 0 && (
                  <span className="shrink-0 font-OpenSansRegular text-[12px] leading-5 text-[#7C829A]">
                    {item.photos.length === 1
                      ? "1 photo"
                      : `${item.photos.length} photos`}
                  </span>
                )}
                <IoIosArrowDown className="text-[#7C829A] shrink-0" size={16} />
              </button>
            )}
          </div>
        );
      })}

      {!disabled && (
        <button
          type="button"
          onClick={add}
          className="self-start mt-0.5 font-OpenSansSemiBold text-[14px] leading-5 text-[#0065FD] cursor-pointer"
        >
          + Add Recommendation
        </button>
      )}

      {viewing && viewingPhotos.length > 0 && (
        <AttachmentImageSlider
          shown
          attachments={viewingPhotos.map((name, index) => ({
            id: index,
            fileName: name,
            displayName: `Photo ${index + 1}`,
          }))}
          activeIndex={viewing.index}
          onCancel={() => setViewing(null)}
        />
      )}
    </div>
  );
}

export default RecommendationList;
